import { Box, Button, Popover, PopoverOrigin, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { track, EVENTS } from '../../lib/analytics';

const STORAGE_KEY = 'pontuax_feature_intros';

const getDismissedFeatures = (): string[] => {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
	} catch {
		return [];
	}
};

const dismissFeature = (id: string) => {
	const dismissed = getDismissedFeatures();
	if (!dismissed.includes(id)) {
		dismissed.push(id);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
	}
};

interface FeatureIntroProps {
	id: string;
	title: string;
	description: string;
	children: ReactNode;
	delay?: number;
	disabled?: boolean;
	onDismiss?: () => void;
	anchorOrigin?: PopoverOrigin;
	transformOrigin?: PopoverOrigin;
	/** Elemento externo p/ ancorar o popover. Necessário quando o filho é position:fixed
	 *  (o wrapper interno colapsa e ancora no lugar errado). Default: wrapper interno. */
	anchorEl?: HTMLElement | null;
}

const FeatureIntro = ({
	id,
	title,
	description,
	children,
	delay = 500,
	disabled = false,
	onDismiss,
	anchorOrigin = { vertical: 'center', horizontal: 'right' },
	transformOrigin = { vertical: 'center', horizontal: 'left' },
	anchorEl,
}: FeatureIntroProps) => {
	const anchorRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);

	// anchorEl externo (filho fixed) tem prioridade; senão usa o wrapper interno
	const resolvedAnchorEl = anchorEl !== undefined ? anchorEl : anchorRef.current;

	useEffect(() => {
		if (disabled) return;
		const dismissed = getDismissedFeatures();
		if (!dismissed.includes(id)) {
			const timer = setTimeout(() => {
				setOpen(true);
				track(EVENTS.FEATURE_INTRO_SHOWN, { intro_id: id });
			}, delay);
			return () => clearTimeout(timer);
		}
	}, [id, delay, disabled]);

	const handleDismiss = () => {
		setOpen(false);
		dismissFeature(id);
		track(EVENTS.FEATURE_INTRO_DISMISSED, { intro_id: id });
		onDismiss?.();
	};

	return (
		<div ref={anchorRef}>
			{children}
			<Popover
				open={open && Boolean(resolvedAnchorEl)}
				anchorEl={resolvedAnchorEl}
				onClose={handleDismiss}
				anchorOrigin={anchorOrigin}
				transformOrigin={transformOrigin}
				slotProps={{
					paper: {
						sx: {
							p: 2,
							maxWidth: 280,
							borderRadius: 2,
							boxShadow: 6,
						},
					},
				}}
			>
				<Typography fontWeight="bold" fontSize="0.9rem" gutterBottom>
					{title}
				</Typography>
				<Typography fontSize="0.8rem" color="text.secondary" sx={{ mb: 1.5 }}>
					{description}
				</Typography>
				<Box display="flex" justifyContent="flex-end">
					<Button
						variant="contained"
						size="small"
						onClick={handleDismiss}
						sx={{ textTransform: 'none', fontWeight: 'bold' }}
					>
						Entendi
					</Button>
				</Box>
			</Popover>
		</div>
	);
};

export default FeatureIntro;
